import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle, Building2, Stethoscope, Heart, FlaskConical } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ESTABLISHMENT_TYPES = [
  { value: 'Clinique privée', label: '🏥 Clinique privée' },
  { value: 'Hôpital', label: '🏨 Hôpital' },
  { value: 'Pharmacie', label: '💊 Pharmacie' },
  { value: 'Laboratoire', label: '🔬 Laboratoire d\'analyses' },
  { value: 'Cabinet médical', label: '🩺 Cabinet médical de groupe' },
  { value: 'Centre de radiologie', label: '📡 Centre de radiologie' },
  { value: 'Autre', label: '🏢 Autre établissement' },
];

const WILAYAS = [
  '01 - Adrar', '02 - Chlef', '03 - Laghouat', '04 - Oum El Bouaghi', '05 - Batna',
  '06 - Béjaïa', '07 - Biskra', '08 - Béchar', '09 - Blida', '10 - Bouira',
  '11 - Tamanrasset', '12 - Tébessa', '13 - Tlemcen', '14 - Tiaret', '15 - Tizi Ouzou',
  '16 - Alger', '17 - Djelfa', '18 - Jijel', '19 - Sétif', '20 - Saïda',
  '21 - Skikda', '22 - Sidi Bel Abbès', '23 - Annaba', '24 - Guelma', '25 - Constantine',
  '26 - Médéa', '27 - Mostaganem', '28 - M\'Sila', '29 - Mascara', '30 - Ouargla',
  '31 - Oran', '32 - El Bayadh', '33 - Illizi', '34 - Bordj Bou Arréridj', '35 - Boumerdès',
  '36 - El Tarf', '37 - Tindouf', '38 - Tissemsilt', '39 - El Oued', '40 - Khenchela',
  '41 - Souk Ahras', '42 - Tipaza', '43 - Mila', '44 - Aïn Defla', '45 - Naâma',
  '46 - Aïn Témouchent', '47 - Ghardaïa', '48 - Relizane', '49 - Timimoune',
  '50 - Bordj Badji Mokhtar', '51 - Ouled Djellal', '52 - Beni Abbès', '53 - In Salah',
  '54 - In Guezzam', '55 - Touggourt', '56 - Djanet', '57 - El M\'Ghair', '58 - El Meniaâ',
  '59 - El Aricha', '60 - Ain Fadda', '61 - Bir El Ater', '62 - Boughzoul', 
'63 - El Adjiba', '64 - El Aouinet', '65 - Hammam Boughrara', '66 - Hassi Messaoud', 
'67 - Ksar El Boukhari', '68 - Sidi Okba', '69 - Theniet El Had',
];

const BENEFITS = [
  {
    icon: '📅',
    title: 'Prise de RDV en ligne 24h/24',
    desc: 'Vos patients réservent à tout moment, même la nuit et le weekend.'
  },
  {
    icon: '📊',
    title: 'Tableau de bord dédié',
    desc: 'Statistiques, revenus et gestion des médecins en temps réel.'
  },
  {
    icon: '🔔',
    title: 'Rappels automatiques',
    desc: 'Réduction des absences grâce aux rappels email avant chaque RDV.'
  },
  {
    icon: '🏥',
    title: 'Page établissement',
    desc: 'Une page dédiée avec tous vos médecins classés par spécialité.'
  },
  {
    icon: '⭐',
    title: 'Avis patients vérifiés',
    desc: 'Renforcez votre réputation avec des avis authentiques.'
  },
  {
    icon: '🌐',
    title: 'Visibilité Google',
    desc: '18 000+ médecins référencés = SEO puissant pour votre établissement.'
  },
];

const Partenariat = () => {
  const [form, setForm] = useState({
    establishment_name: '',
    establishment_type: '',
    wilaya: '',
    phone: '',
    email: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.establishment_type) {
      toast.error('Veuillez sélectionner le type d\'établissement');
      return;
    }
    if (!form.wilaya) {
      toast.error('Veuillez sélectionner votre wilaya');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/partnership`, form);
      setSent(true);
      toast.success('Demande envoyée ! Nous vous contacterons dans les 48 heures.');
    } catch (error) {
      toast.error("Erreur lors de l'envoi. Réessayez ou contactez-nous par email.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />

      {/* Hero */}
      <section className="bg-gradient-to-r from-[#0596DE] to-[#047CBA] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full mb-6">
            <Building2 className="w-4 h-4" />
            <span className="text-sm font-medium">Partenariats établissements</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold mb-4">
            Rejoignez Tabbibak.dz
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            Digitalisez la prise de rendez-vous de votre établissement et offrez à vos patients une expérience moderne, disponible 24h/24.
          </p>
        </div>
      </section>

      {/* Avantages */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A]">Ce que Tabbibak apporte à votre établissement</h2>
            <p className="text-[#64748B] mt-3">Une solution complète pour moderniser votre accueil patient</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {BENEFITS.map((b, i) => (
              <div key={i} className="bg-[#F8FAFC] rounded-2xl p-6 border border-[#E2E8F0] hover:shadow-md transition-all">
                <div className="text-3xl mb-4">{b.icon}</div>
                <h3 className="font-semibold text-[#0F172A] mb-2">{b.title}</h3>
                <p className="text-sm text-[#64748B] leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Offre */}
      <section className="py-12 bg-[#F8FAFC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#0596DE] rounded-3xl p-8 text-white text-center">
            <h2 className="text-2xl font-bold mb-2">Offre de lancement</h2>
            <p className="text-white/80 mb-6">Pour les établissements partenaires fondateurs</p>
            <div className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto">
              <div className="bg-white/20 rounded-2xl p-6">
                <div className="text-3xl font-bold mb-1">3 mois</div>
                <div className="text-white/80 text-sm">Essai gratuit — tous vos médecins inclus</div>
              </div>
              <div className="bg-white/20 rounded-2xl p-6">
                <div className="text-3xl font-bold mb-1">Tarif préférentiel</div>
                <div className="text-white/80 text-sm">Abonnement partenaire fondateur</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Formulaire */}
      <section className="py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A]">Demande de partenariat</h2>
            <p className="text-[#64748B] mt-2">Remplissez ce formulaire et nous vous contacterons dans les 48 heures.</p>
          </div>

          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-8 shadow-sm">
            {sent ? (
              <div className="text-center py-12">
                <CheckCircle className="w-16 h-16 text-[#10B981] mx-auto mb-4" />
                <h2 className="text-xl font-bold text-[#0F172A] mb-2">Demande envoyée !</h2>
                <p className="text-[#64748B] mb-6">
                  Nous avons bien reçu votre demande. Notre équipe vous contactera dans les 48 heures.
                </p>
                <Button onClick={() => setSent(false)} variant="outline" className="rounded-full">
                  Envoyer une autre demande
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label className="mb-1 block">Nom de l'établissement *</Label>
                  <Input
                    name="establishment_name"
                    value={form.establishment_name}
                    onChange={handleChange}
                    placeholder="Ex: Clinique El Shifa, Hôpital Chahids Mahmoudi..."
                    required
                    className="rounded-xl"
                  />
                </div>

                <div>
                  <Label className="mb-1 block">Type d'établissement *</Label>
                  <select
                    name="establishment_type"
                    value={form.establishment_type}
                    onChange={handleChange}
                    required
                    className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0596DE] bg-white"
                  >
                    <option value="">Sélectionner le type...</option>
                    {ESTABLISHMENT_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label className="mb-1 block">Wilaya *</Label>
                  <select
                    name="wilaya"
                    value={form.wilaya}
                    onChange={handleChange}
                    required
                    className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0596DE] bg-white"
                  >
                    <option value="">Sélectionner la wilaya...</option>
                    {WILAYAS.map((w) => (
                      <option key={w} value={w}>{w}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-1 block">Téléphone *</Label>
                    <Input
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="+213 XX XX XX XX"
                      required
                      className="rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="mb-1 block">Email professionnel *</Label>
                    <Input
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="contact@etablissement.dz"
                      required
                      className="rounded-xl"
                    />
                  </div>
                </div>

                <div>
                  <Label className="mb-1 block">Message (optionnel)</Label>
                  <Textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Nombre de médecins, besoins spécifiques, questions..."
                    className="min-h-[120px] rounded-xl"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-xl bg-[#0596DE] hover:bg-[#047CBA] h-12 text-base font-semibold text-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {loading ? (
                    <span className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Envoyer ma demande
                    </>
                  )}
                </button>
              </form>
            )}

            <div className="mt-8 pt-6 border-t border-[#E2E8F0] grid md:grid-cols-2 gap-4 text-sm text-[#64748B]">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#0596DE]" />
                <span>contact@tabbibak.com</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#0596DE]" />
                <span>Tizi Ouzou & Oran, Algérie</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F172A] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="font-bold text-xl font-['Outfit'] text-[#0596DE]">Tabbibak.dz</span>
          <p className="text-gray-400 text-sm mt-3">La plateforme algérienne de prise de rendez-vous médicaux</p>
          <div className="border-t border-gray-800 mt-6 pt-6 text-gray-500 text-sm">
            © {new Date().getFullYear()} Tabbibak.dz
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Partenariat;